import { useState, useRef, useEffect } from "react";
import { MessageCircle, X, Send, Loader2 } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { api } from "../lib/api";

export default function ChatbotWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      id: "welcome",
      role: "assistant",
      content:
        "Hi! I'm the Paralegal Hub assistant. Ask me about our services or how we can help. For case-specific questions, I'll point you to our booking page.",
    },
  ]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, open]);

  const send = async () => {
    const text = input.trim();
    if (!text || sending) return;
    const userMsgId = `u-${Date.now()}`;
    setMessages((m) => [...m, { id: userMsgId, role: "user", content: text }]);
    setInput("");
    setSending(true);
    try {
      const { data } = await api.post("/chatbot", { message: text, session_id: sessionId });
      setSessionId(data.session_id);
      setMessages((m) => [...m, { id: `a-${Date.now()}`, role: "assistant", content: data.reply }]);
    } catch (e) {
      console.error("Chatbot error:", e);
      setMessages((m) => [
        ...m,
        { id: `err-${Date.now()}`, role: "assistant", content: "Sorry, the assistant is temporarily unavailable. Please try again shortly." },
      ]);
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      {/* Floating button (positioned above the Emergent badge) */}
      <button
        onClick={() => setOpen(!open)}
        className="fixed bottom-24 right-6 z-[60] h-14 w-14 rounded-sm bg-blue-700 hover:bg-blue-600 text-white shadow-lg flex items-center justify-center transition-all hover:-translate-y-0.5"
        aria-label="Open chat assistant"
        data-testid="chatbot-toggle"
      >
        {open ? <X className="h-5 w-5" /> : <MessageCircle className="h-5 w-5" strokeWidth={1.5} />}
      </button>

      {open && (
        <div
          className="fixed bottom-44 right-6 z-[60] w-[92vw] max-w-md h-[70vh] max-h-[600px] bg-zinc-950 border border-zinc-800 rounded-sm shadow-xl flex flex-col"
          data-testid="chatbot-panel"
        >
          <div className="bg-blue-700 text-white px-5 py-4 flex items-center justify-between">
            <div>
              <p className="font-medium text-sm">Paralegal Assistant</p>
              <p className="text-xs text-blue-200">Powered by AI · Not legal advice</p>
            </div>
            <button onClick={() => setOpen(false)} aria-label="close chat" data-testid="chatbot-close">
              <X className="h-4 w-4" />
            </button>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto p-5 space-y-3" data-testid="chatbot-messages">
            {messages.map((m) => (
              <div
                key={m.id}
                className={`max-w-[85%] px-4 py-2.5 rounded-sm text-sm leading-relaxed ${
                  m.role === "user"
                    ? "ml-auto bg-blue-700 text-white"
                    : "bg-zinc-900 text-zinc-100"
                }`}
              >
                {m.content}
              </div>
            ))}
            {sending && (
              <div className="bg-zinc-900 text-zinc-400 px-4 py-2.5 rounded-sm text-sm inline-flex items-center gap-2">
                <Loader2 className="h-3 w-3 animate-spin" /> Thinking...
              </div>
            )}
          </div>

          <div className="border-t border-zinc-800 p-3 flex gap-2">
            <Input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder="Ask a question..."
              className="rounded-sm border-zinc-700"
              disabled={sending}
              data-testid="chatbot-input"
            />
            <Button
              onClick={send}
              disabled={sending || !input.trim()}
              className="rounded-sm bg-blue-700 hover:bg-blue-600"
              data-testid="chatbot-send"
            >
              <Send className="h-4 w-4" strokeWidth={1.5} />
            </Button>
          </div>
        </div>
      )}
    </>
  );
}
